import Grid from '@material-ui/core/Grid';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/common/Buttons';
import { ModalDialog } from '../../../../components/common/Dialogs';
import { useTrans } from '../../../../services/i18n';
import { SpacingContainer } from '../../../../components/common/Containers';
import connect from './connect';
import Typography from '../../../common/Typography';
import { APP_MENU } from '../../../../constants';
import * as MENU_ICONS from '../../../../components/svg/Icons/Menu';
import { IconDashboard } from '../../../../components/svg/Icons/Menu';
import { Divider } from '../../../common/Divider';
import { ReactComponent as CurrentStepIcon } from './icons/currentStep.svg';
import { ReactComponent as PassStepIcon } from './icons/passStep.svg';
import { ReactComponent as InactiveStepIcon } from './icons/step.svg';
import { useApplication } from '../../../../services/application';

const styles = {
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    textTransform: 'capitalize',
  },
  titleIconBadge: {
    background: '#F32445',
    color: '#FFFFFF',
    marginRight: '16px',
    padding: '5px 6px 2px 6px',
    borderRadius: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  descriptionText: {
    color: '#888FA9',
    lineHeight: 1.6,
  },
  rowsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
  },
  onboardingRow: {
    display: 'grid',
    gridTemplateColumns: '320px minmax(0, 1fr)',
    columnGap: '32px',
    alignItems: 'start',
    width: '100%',
  },
  onboardingLeft: {
    display: 'grid',
    gridTemplateColumns: '44px minmax(0, 1fr)',
    columnGap: '16px',
    alignItems: 'start',
    minWidth: 0,
  },
  onboardingIconWrap: {
    width: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#4E5978',
    paddingTop: '2px',
  },
  onboardingLabel: {
    color: '#4E5978',
    lineHeight: 1.4,
    wordBreak: 'break-word',
  },
  onboardingRight: {
    color: '#888FA9',
    lineHeight: 1.6,
    minWidth: 0,
    wordBreak: 'break-word',
  },
  stepDot: {
    margin: '0 3px',
    display: 'inline-flex',
    alignItems: 'center',
  },
};

const OnboardingModal = ({ open, onClose, changeCurrentOnboardingStep, updateOnViewTour, subscription }) => {
  const { trans } = useTrans();
  const application = useApplication();
  const [tourStep, setTourStep] = useState(0);
  const { currentPlan } = subscription?.data || {};

  const isValidPlan = useMemo(() => {
    return !['', 'expired'].includes(currentPlan);
  }, [currentPlan]);

  const closeModal = () => {
    onClose();
    updateOnViewTour({ onViewTour: 1 });
    changeCurrentOnboardingStep({ step: 0 });
  };

  const beginTour = async () => {
    await application.call('user.markOnViewTour', {});
    setTourStep(1);
    changeCurrentOnboardingStep({ step: 1 });
  };

  const nextStep = () => {
    const n = tourStep + 1;
    setTourStep(n);
    changeCurrentOnboardingStep({ step: n });

    if (n > 4) {
      closeModal();
    }
  };

  const skipTour = async () => {
    await application.call('user.markOnViewTour', {});
    closeModal();
  };

  const IconC = (item) => {
    const Icon = item && tourStep ? MENU_ICONS[item.icon] || IconDashboard : IconDashboard;

    return <Icon />;
  };

  const StepIcon = ({ step }) => {
    if (step === tourStep) {
      return <CurrentStepIcon />;
    }

    if (step < tourStep) {
      return <PassStepIcon />;
    }

    return <InactiveStepIcon />;
  };

  useEffect(() => {
    if (open && isValidPlan) {
      setTourStep(0);
    }
  }, [open, isValidPlan]);

  const currentMenu = APP_MENU[tourStep - 1];

  return (
    <ModalDialog
      open={open && isValidPlan}
      onClose={closeModal}
      title={
        tourStep === 0 ? (
          trans('a-quick-and-efficient-tour')
        ) : (
          <span style={styles.titleWrapper}>
            <span style={styles.titleIconBadge}>{IconC(currentMenu)}</span>
            {trans(currentMenu?.key)}
          </span>
        )
      }
      actions={
        <Grid container spacing={2} justifyContent="space-between" alignItems="center">
          <Grid item>
            {tourStep > 0 &&
              [1, 2, 3, 4].map((i) => {
                return (
                  <span style={styles.stepDot} key={i}>
                    <StepIcon step={i} />
                  </span>
                );
              })}
          </Grid>

          <Grid item>
            {tourStep === 0 ? (
              <>
                <Button variant="outlined" color="primary" onClick={skipTour} style={{ marginRight: '16px' }}>
                  {trans('skip')}
                </Button>

                <Button variant="contained" color="primary" onClick={beginTour}>
                  {trans('begin-tour')}
                </Button>
              </>
            ) : (
              <Button variant="contained" color="primary" onClick={nextStep}>
                {tourStep === 4 ? trans('finish-tour') : trans('continue')}
              </Button>
            )}
          </Grid>
        </Grid>
      }
    >
      <SpacingContainer px={3} py={0}>
        {tourStep === 0 && (
          <div>
            <Typography variant="h3" weight="bold">
              {trans('welcome-to-usfcr')}!
            </Typography>

            <br />

            <Typography variant="body1">{trans('welcome-to-usfcr-des')}</Typography>
          </div>
        )}

        {tourStep > 0 && (
          <div>
            <Typography style={styles.descriptionText} component="span" fontFamily="lexend" variant="body2">
              {currentMenu?.onboardingDes}
            </Typography>

            <Divider light style={{ margin: '16px 0 24px' }} />

            <div style={styles.rowsWrapper}>
              {currentMenu &&
                currentMenu.items
                  .filter((i) => i.key !== 'demo-dashboard')
                  .map((i, index) => {
                    const IconComponent = MENU_ICONS[i.icon] || IconDashboard;

                    if (!i.onboardingDes) {
                      return null;
                    }

                    return (
                      <div key={`${i.key}-${index}`} style={styles.onboardingRow}>
                        <div style={styles.onboardingLeft}>
                          <div style={styles.onboardingIconWrap}>
                            <IconComponent />
                          </div>

                          <Typography
                            style={styles.onboardingLabel}
                            component="div"
                            fontFamily="lexend"
                            variant="body2"
                          >
                            {trans(i.key)}
                          </Typography>
                        </div>

                        <Typography style={styles.onboardingRight} component="div" fontFamily="lexend" variant="body2">
                          {i.onboardingDes ?? ''}
                        </Typography>
                      </div>
                    );
                  })}
            </div>
          </div>
        )}
      </SpacingContainer>
    </ModalDialog>
  );
};

export default connect(OnboardingModal);
